"use client";

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { usePathname } from "next/navigation";

import { FlightSpinLoader } from "@repo/ui/components/common/Loader/FlightSpinLoader";

type LoadingContextType = {
  isPageRefreshing: boolean;
  setIsPageRefreshing: (value: boolean) => void;
  /**
   * Hold the overlay for a navigation that has been STARTED, released when the new route
   * lands (or by `endNavigation`, or by the ceiling below).
   *
   * Separate from `isPageRefreshing` on purpose. That flag is a single boolean with ~90
   * call sites, and each owner clears it unconditionally when its own work finishes — so a
   * component that happens to be mounted can switch off an overlay it did not raise. That
   * is exactly what happened on the flight page: the country picker raised the loader,
   * `validate()` dispatched `resetApp()`, and the availability effect in `Flights.tsx`
   * re-ran and cleared the loader 4.9s before the navigation committed, leaving 6.6s of
   * blank page. A navigation hold cannot be released by another owner's bookkeeping.
   */
  beginNavigation: () => void;
  /** Release the hold without waiting for a route change — use when the start failed. */
  endNavigation: () => void;
};

const LoadingContext = createContext<LoadingContextType | null>(null);

export const useGlobalLoading = () => {
  const ctx = useContext(LoadingContext);
  if (!ctx) throw new Error("useGlobalLoading must be used within LoadingProvider");
  return ctx;
};

/**
 * Hard ceiling on a navigation hold. The overlay is a full-viewport, interaction-blocking
 * layer, so a navigation that never lands must not strand the user behind it — this
 * codebase has shipped a permanently-stuck loader before (8492f273e).
 *
 * Deliberately applies ONLY to the navigation hold. Putting a timeout on
 * `isPageRefreshing` would silently change behaviour for every existing caller, including
 * ones whose work legitimately runs longer than this.
 */
const NAVIGATION_HOLD_MAX_MS = 10_000;

/**
 * Releases the navigation hold when a navigation actually LANDS, not when it is started.
 *
 * A caller that shows the loader and then calls router.push cannot await the result —
 * router.push resolves nothing useful — so clearing in its own `finally` hides the overlay
 * the instant the push begins and leaves the user staring at the old page for the whole RSC
 * transition (~2s on a country change from the home page, 6.6s from the flight page).
 * Callers leave the hold in place instead and the arriving route releases it here.
 *
 * ⚠️ This MUST stay a leaf component rather than a `usePathname()` call inside
 * LoadingProvider. Reading the router context in the provider subscribes it to Next's
 * router, which entangles the provider's own state updates with whatever transition is
 * pending.
 */
function NavigationHoldRelease({ onNavigated }: { onNavigated: () => void }) {
  const pathname = usePathname();
  const settledPath = useRef(pathname);

  useEffect(() => {
    if (pathname === settledPath.current) return;
    settledPath.current = pathname;
    onNavigated();
  }, [pathname, onNavigated]);

  return null;
}

export function LoadingProvider({ children }: { children: ReactNode }) {
  const [isPageRefreshing, setIsPageRefreshing] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const beginNavigation = useCallback(() => setIsNavigating(true), []);
  const endNavigation = useCallback(() => setIsNavigating(false), []);

  useEffect(() => {
    if (!isNavigating) return;
    const timer = setTimeout(() => setIsNavigating(false), NAVIGATION_HOLD_MAX_MS);
    return () => clearTimeout(timer);
  }, [isNavigating]);

  return (
    <LoadingContext.Provider
      value={{ isPageRefreshing, setIsPageRefreshing, beginNavigation, endNavigation }}
    >
      {children}
      <NavigationHoldRelease onNavigated={endNavigation} />
      {(isPageRefreshing || isNavigating) && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-white/50"
          data-testid="page-loader"
        >
          <FlightSpinLoader size="large" />
        </div>
      )}
    </LoadingContext.Provider>
  );
}
